import { useCallback, useEffect, useState } from 'react';
import {
  Colors,
  Flex,
  IconClose,
  PrimaryButton,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type { TasteOnboardingCardProps } from './types';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { isNotNil, localStorageBridge } from '../../../../utils';
import { Platform } from 'react-native';

// 닫기 시각을 저장하는 스토리지 키. Android 네이티브(OnboardingCardStateImpl)가 쓰는 키와
// 동일 — Android LocalStorageBridge 의 getInt/putInt 는 내부적으로 preferenceProvider 의
// Long(getLong/putLong)으로 매핑되므로, 같은 슬롯을 공유해 네이티브와 dismiss 상태가 연동된다.
// (iOS 는 네이티브가 별도 키/UserDefaults 를 써서 완전 공유는 아니고, RN 자체 영속화로 동작)
const ONBOARDING_LAST_CLOSED_KEY = 'TODAY_ONBOARDING_LAST_CHECKED_TIME';

/** 두 epoch(ms)가 로컬 기준 같은 날짜인지. (Android equalsDateWithoutTime 과 동일 의미) */
const isSameLocalDay = (a: number, b: number): boolean => {
  const dateA = new Date(a);
  const dateB = new Date(b);
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

// Android(OnboardingComponent.SampleImages)와 동일한 고정 5칸 배치.
// 슬롯 순서(좌→우)가 참조하는 images 인덱스: [3, 1, 0, 2, 4].
// 중앙(images[0])만 64, 나머지 48. 인접 -8 겹침, 중앙일수록 앞(zIndex).
// 해당 인덱스 이미지가 없으면 빈 슬롯(자리만 유지)으로 둬 배치가 흔들리지 않게 한다.
const ONBOARDING_IMAGE_SLOTS = Platform.select({
  ios: [
    { index: 3, size: 48, zIndex: 1 },
    { index: 1, size: 48, zIndex: 2 },
    { index: 2, size: 64, zIndex: 3 },
    { index: 0, size: 48, zIndex: 2 },
    { index: 4, size: 48, zIndex: 1 },
  ],
  default: [
    { index: 3, size: 48, zIndex: 1 },
    { index: 1, size: 48, zIndex: 2 },
    { index: 0, size: 64, zIndex: 3 },
    { index: 2, size: 48, zIndex: 2 },
    { index: 4, size: 48, zIndex: 1 },
  ],
});

export const TasteOnboardingCard = ({
  logging,
  body,
}: TasteOnboardingCardProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const [dismissed, setDismissed] = useState(false);
  // null = 스토리지 확인 전, true = 오늘 이미 닫음(숨김), false = 노출 가능.
  const [closedToday, setClosedToday] = useState<boolean | null>(null);
  const item = body;

  const logAnalyticsEvent = useLogAnalyticsEvent();

  // 마지막 닫은 시각이 "오늘"이면 노출하지 않는다(하루 1회). Android 와 키를 공유한다.
  useEffect(() => {
    let active = true;
    localStorageBridge
      .getInt(ONBOARDING_LAST_CLOSED_KEY)
      .then(lastClosed => {
        if (!active) return;
        setClosedToday(
          isNotNil(lastClosed) &&
            lastClosed > 0 &&
            isSameLocalDay(lastClosed, Date.now()),
        );
      })
      .catch(() => {
        if (active) setClosedToday(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const visible = isNotNil(item) && !dismissed && closedToday === false;

  useEffect(() => {
    if (!visible || !item) return;
    logAnalyticsEvent({
      name: 'DISPLAY_ONBOARDING',
      analytics: { ...logging?.analytics, ...item.logging?.analytics },
      ably_only_log: {
        ...logging?.ably_only_log,
        ...item.logging?.ably_only_log,
      },
    });
  }, [visible, item, logging, logAnalyticsEvent]);

  const handlePressButton = useCallback(() => {
    if (!item) return;
    logAnalyticsEvent({
      name: 'CLICK_ONBOARDING',
      analytics: { ...logging?.analytics, ...item.logging?.analytics },
      ably_only_log: {
        ...logging?.ably_only_log,
        ...item.logging?.ably_only_log,
      },
    });
    openDeeplink(item.item.button.deeplink);
  }, [item, logging, openDeeplink, logAnalyticsEvent]);

  const handleClose = useCallback(() => {
    setDismissed(true);
    // 오늘은 다시 뜨지 않도록 닫은 시각 저장(Android 네이티브와 공유되는 키).
    // 저장 실패해도 세션 내 숨김(dismissed)은 유지된다.
    localStorageBridge
      .putInt(ONBOARDING_LAST_CLOSED_KEY, Date.now())
      .catch(() => {});
  }, []);

  if (!visible) return null;

  const { images, title, description, button } = body.item;

  return (
    <Flex margin={{ horizontal: 12, bottom: 16 }}>
      <Flex
        backgroundColor="bg_secondary"
        borderRadius={8}
        padding={{ top: 32, bottom: 20, horizontal: 16 }}
        alignItems="center"
      >
        {/* 닫기 버튼 */}
        <Flex
          position="absolute"
          top={8}
          right={8}
          width={32}
          height={32}
          alignItems="center"
          justifyContent="center"
          onPress={handleClose}
        >
          <IconClose size={20} fill={Colors.content_secondary} />
        </Flex>

        {/* 이미지 overlapping — Android SampleImages 와 동일한 고정 5칸 배치 */}
        {images.length > 0 && (
          <Flex.Row
            alignItems="center"
            justifyContent="center"
            margin={{ bottom: 12 }}
          >
            {ONBOARDING_IMAGE_SLOTS.map((slot, slotIndex) => {
              const img = images[slot.index];
              return (
                <Flex
                  key={slot.index}
                  width={slot.size}
                  height={slot.size}
                  borderRadius={slot.size / 2}
                  overflow="hidden"
                  zIndex={slot.zIndex}
                  margin={{ left: slotIndex > 0 ? -8 : 0 }}
                >
                  {isNotNil(img) && (
                    <RemoteImage
                      uri={img}
                      width={slot.size}
                      height={slot.size}
                      resizeMode="contain"
                    />
                  )}
                </Flex>
              );
            })}
          </Flex.Row>
        )}

        <Typography type="subtitle1" color="content_primary" textAlign="center">
          {title}
        </Typography>
        <Flex margin={{ top: 4, bottom: 12 }}>
          <Typography type="body4" color="content_secondary" textAlign="center">
            {description}
          </Typography>
        </Flex>

        <Flex alignSelf="center">
          <PrimaryButton size="small" color="gray" onPress={handlePressButton}>
            {button.title}
          </PrimaryButton>
        </Flex>
      </Flex>
    </Flex>
  );
};
